import { useDisclosure } from '@chakra-ui/react';
import {
  BlueprintQuoteResponse,
  ContractStatusEnum,
  CrmOpportunityForInput,
  HubspotAssociatedCompany,
  IAccountRespSchema,
  IAccountSchema,
  IContractWithQuotes,
  ICustomFieldRecordSchema,
  ILegalEntityResponseSchema,
  IQuoteRequestSchemaExtended,
  IQuoteRespSchema,
  QuoteAdditionalFields,
  QuoteTypeEnum,
} from '@monetize/types/app';
import { Maybe } from '@monetize/types/common';
import { ExperienceConfigurationKeys } from '@monetize/types/experience-config';
import hasValue from 'lodash/has';
import isString from 'lodash/isString';
import { FC, useCallback, useEffect, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useAtomValue } from 'jotai';
import { handleApiErrorToast } from '../../../../../api/axios';
import {
  doAmendContract,
  doRenewContract,
  useGetOrCreateOpportunityWithCrmLink,
} from '../../../../../api/cpqService';
import { AccountSelect } from '../../../../../components/Account/AccountSelect';
import { BlueprintSelect } from '../../../../../components/Blueprint/BlueprintSelect';
import {
  MBox,
  MButton,
  MCenterModal,
  MCurrencySelect,
  MFlex,
  MFormField,
  MHStack,
  MRadio,
  MRadioGroup,
  MStack,
  MText,
  MTooltip,
  MVStack,
} from '../../../../../components/Monetize';
import { MCustomSelect } from '../../../../../components/Monetize/MCustomSelect/MCustomSelect';
import { RenewalModal } from '../../../../../components/RenewalModal/RenewalModal';
import { NEW_CONTRACT_SLUG } from '../../../../../constants/quotes';
import { getQuoteEditRoute } from '../../../../../constants/routes';
import { useExperienceConfiguration } from '../../../../../hooks';
import { useBackNavigate } from '../../../../../hooks/useBackNavigate';
import { useACL } from '../../../../../services/acl/acl';
import { logger } from '../../../../../services/logger';
import { appGlobalDataState } from '../../../../../store/global.store';
import {
  canAmendContract,
  canRenewContract,
} from '../../../../../utils/contracts';
import { getQuoteDescription } from '../../quoteUtils';
import { ContractSelectField } from './ContractSelectField';
import { HubspotLinkedAccountSelect } from './HubspotLinkedAccountSelect';
import NewAccountForm from './NewAccountForm';
import { OpportunitySearchInput } from './OpportunitySearchInput';

export interface NewQuoteFormDataTypes {
  isOpen: boolean;
  loading: boolean;
  accountById: IAccountRespSchema | null;
  fetchAccountById: (accountId: string) => Promise<IAccountRespSchema | null>;
  onOpen: () => void;
  onClose: () => void;
}

interface NewQuoteFormProps {
  isOpen: boolean;
  hasGuidedSellingProcess?: boolean;
  fixedAccount: IAccountRespSchema | null;
  legalEntities: ILegalEntityResponseSchema[];
  existingOpportunityId?: Maybe<string>;
  onClose: () => void;
  closeAndContinue: ({
    crmOpportunity,
    customFields,
    selectedBlueprintId,
    hubspotLinkedCompany,
  }: {
    crmOpportunity?: Maybe<CrmOpportunityForInput>;
    customFields?: ICustomFieldRecordSchema;
    useGuidedSelling: boolean;
    selectedBlueprintId?: string;
    hubspotLinkedCompany?: HubspotAssociatedCompany | null;
  }) => Promise<void>;
  onCreateNewAccount?: (account: IAccountSchema) => Promise<IAccountRespSchema>;
  crmCustomFields?: Record<string, any>;
  /** Currency mapped from the CRM, preferred over the account default for new contracts */
  crmCurrency?: string;
  additionalFields?: QuoteAdditionalFields;
}

export const NewQuoteForm: FC<NewQuoteFormProps> = ({
  isOpen,
  hasGuidedSellingProcess = false,
  onClose,
  closeAndContinue,
  existingOpportunityId,
  fixedAccount: internalFixedAccount,
  legalEntities,
  onCreateNewAccount,
  crmCustomFields = {},
  crmCurrency,
  additionalFields = {},
}: NewQuoteFormProps) => {
  const {
    hasMultipleCurrency,
    hasMultipleLegalEntity,
    hasCrmConfigured,
    hasHubspotConfigured,
  } = useAtomValue(appGlobalDataState);
  const { control, watch, formState, getValues, setValue } =
    useFormContext<IQuoteRequestSchemaExtended>();
  const [isNewAccount, setIsNewAccount] = useState<boolean>(false);
  const [newAccount, setNewAccount] = useState<IAccountSchema>();
  const [crmOpportunity, setCrmOpportunity] =
    useState<CrmOpportunityForInput | null>(null);
  const [customFields, setCustomFields] =
    useState<ICustomFieldRecordSchema>(crmCustomFields);
  const [fixedAccount, setFixedAccount] = useState<IAccountRespSchema | null>(
    internalFixedAccount,
  );
  const [contracts, setContracts] = useState<IContractWithQuotes[]>([]);
  const [isContractLoading, setIsContractLoading] = useState(false);
  const [selectedContract, setSelectedContract] =
    useState<IContractWithQuotes>();
  const [isExistingContractSelected, setIsExistingContractSelected] =
    useState<boolean>(false);
  const [isNewQuoteLoading, setIsNewQuoteLoading] = useState<boolean>(false);
  const [isNewAccountLoading, setIsNewAccountLoading] =
    useState<boolean>(false);
  const [selectedBlueprintId, setSelectedBlueprintId] = useState<string>('');
  const [hubspotLinkedCompany, setHubspotLinkedCompany] =
    useState<HubspotAssociatedCompany | null>(null);
  const { navigate, navigateBack } = useBackNavigate(1);
  const { mutateAsync: doCreateOrGetOpportunity } =
    useGetOrCreateOpportunityWithCrmLink();

  const experienceConfig = useExperienceConfiguration(
    ExperienceConfigurationKeys.quoteAndContractSettings,
  );
  const enableEarlyRenewal = experienceConfig?.enableEarlyRenewal;

  const {
    open: isOpenRenewalModal,
    onOpen: onOpenRenewalModal,
    onClose: onCloseRenewalModal,
  } = useDisclosure();

  const selectedAccountOption = watch('account');
  const selectedContractId = watch('contractId');
  const selectedQuoteType = watch('quoteType');
  const selectedCurrency = watch('currency');

  const isAccountSelected = !!(selectedAccountOption || internalFixedAccount);

  const isNewContractSelected = selectedContractId === NEW_CONTRACT_SLUG;
  const isContractSelected = !!selectedContractId;
  const isRenewal = selectedQuoteType === QuoteTypeEnum.RENEWAL;
  const isAmendOrRenew =
    selectedQuoteType === QuoteTypeEnum.AMENDMENT || isRenewal;

  const { canDo } = useACL();

  useEffect(() => {
    setIsExistingContractSelected(!!selectedContract && !isNewContractSelected);
  }, [selectedContract, isNewContractSelected]);

  useEffect(() => {
    !selectedContract && setValue('quoteType', '');
  }, [selectedContract, setValue]);

  useEffect(() => {
    if (isAmendOrRenew) {
      setSelectedBlueprintId('');
    }
  }, [isAmendOrRenew]);

  // Handle blueprint selection
  const handleBlueprintChange = (blueprint: BlueprintQuoteResponse | null) => {
    if (blueprint) {
      setSelectedBlueprintId(blueprint.id);
      // Set currency from blueprint and mark contract as new
      setValue('currency', blueprint.quoteCurrency);
      setValue('contractId', NEW_CONTRACT_SLUG);
      setValue('quoteType', '');
    } else {
      setSelectedBlueprintId('');
    }
  };

  useEffect(() => {
    (async () => {
      if (additionalFields.linkedAccountId) {
        try {
          setValue('linkedAccountId', additionalFields.linkedAccountId);
        } catch (err) {
          logger.error('Error setting secondary account', err);
        }
      }
    })();
  }, [additionalFields.linkedAccountId, setValue]);

  useEffect(() => {
    if (fixedAccount) {
      // account passed in props, user cannot change the selected account
      setValue('account', fixedAccount);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fixedAccount]);

  const setDescription = (accountName: string) => {
    setValue('description', getQuoteDescription(accountName));
  };

  const setNewContractValues = () => {
    const allValues = getValues();
    if (!hasValue(allValues, 'account')) {
      return;
    }
    setValue('accountId', (allValues?.account as IAccountRespSchema)?.id);
    setDescription(
      (allValues?.account as IAccountRespSchema)?.accountName || '',
    );
  };

  const handleNewAccountChange = useCallback((account: IAccountSchema) => {
    setNewAccount(account);
    setValue('currency', account.defaultCurrency);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onClearAccountValue = () => {
    setValue('account', '');
    setValue('billGroupId', '');
    setValue('contractId', '');
    setSelectedContract(undefined);
  };

  const submitModal = useCallback(
    async (useGuidedSelling: boolean) => {
      if (
        isRenewal &&
        enableEarlyRenewal &&
        selectedContract?.status !== ContractStatusEnum.FINISHED
      ) {
        onOpenRenewalModal();
        return;
      }

      try {
        if (isNewAccount && newAccount && onCreateNewAccount) {
          setIsNewAccountLoading(true);
          const account = await onCreateNewAccount(newAccount);
          setValue('legalEntityId', account.defaultLegalEntityId);
          setIsNewAccount(false);
          setFixedAccount(account as unknown as IAccountRespSchema);
          setIsNewAccountLoading(false);
          setNewContractValues();
          return;
        }

        setIsNewQuoteLoading(true);
        if (!isNewAccount && !isExistingContractSelected) {
          setNewContractValues();
          await closeAndContinue({
            crmOpportunity,
            customFields,
            useGuidedSelling,
            selectedBlueprintId,
            hubspotLinkedCompany,
          });
          setIsNewQuoteLoading(false);
          return;
        }

        // instead of setting values on the underlying new quote form, user is amending or renewing an existing quote,
        // so we call the endpoint that creates the new quote, then open it:
        const { account, quoteType } = getValues();
        const contractId = selectedContract?.id;
        if (!contractId) {
          throw new Error('Cannot find contract id');
        }

        let newQuote: IQuoteRespSchema | null = null;

        let opportunityId = existingOpportunityId;

        /**
         * If the user has selected a CRM opportunity, then use that opportunity
         */
        if (crmOpportunity && account && !isString(account)) {
          const opportunity = await doCreateOrGetOpportunity({
            customId: crmOpportunity.id,
            createPayload: {
              accountId: account.id,
              name: crmOpportunity.name,
              customId: crmOpportunity.id,
            },
          });
          opportunityId = opportunity.id;
        }

        switch (quoteType) {
          case QuoteTypeEnum.AMENDMENT:
            newQuote = await doAmendContract(
              contractId,
              opportunityId ? { id: opportunityId } : undefined,
              undefined,
              additionalFields,
            );
            break;
          case QuoteTypeEnum.RENEWAL:
            newQuote = await doRenewContract(
              contractId,
              opportunityId
                ? { opportunity: { id: opportunityId }, additionalFields }
                : undefined,
            );
            break;
          default:
            throw new Error('Invalid quote type');
        }
        if (newQuote?.id) {
          navigate(getQuoteEditRoute(newQuote.id));
        }
      } catch (error) {
        handleApiErrorToast(error);
      } finally {
        setIsNewQuoteLoading(false);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [
      isRenewal,
      enableEarlyRenewal,
      onOpenRenewalModal,
      isNewAccount,
      newAccount,
      onCreateNewAccount,
      isExistingContractSelected,
      getValues,
      selectedContract?.id,
      existingOpportunityId,
      crmOpportunity,
      setValue,
      setNewContractValues,
      closeAndContinue,
      customFields,
      doCreateOrGetOpportunity,
      navigate,
      selectedBlueprintId,
    ],
  );

  const handleModalCancel = () => {
    if (isNewAccount) {
      setIsNewAccount(false);
      return;
    }
    return navigateBack();
  };

  // if New Account created from New Quote modal must have accountName otherwise button disable
  const isNewAccountWithoutName = isNewAccount && !newAccount?.accountName;
  // if existing contract selected you must choose Amend/Renew otherwise button disable
  const isExistingContractWithoutQuoteType =
    isExistingContractSelected && !selectedQuoteType;
  // if New Account created from New Quote modal must have accountName otherwise button disable
  const isNeitherNewAccountNorContractSelected =
    !isExistingContractSelected && !isNewAccount && !isNewContractSelected;

  const isSubmitButtonDisable =
    isNewQuoteLoading ||
    isNewAccountWithoutName ||
    isExistingContractWithoutQuoteType ||
    isNeitherNewAccountNorContractSelected;

  if (selectedContract && isOpenRenewalModal) {
    return (
      <RenewalModal
        contract={selectedContract}
        crmOpportunity={crmOpportunity}
        existingOpportunityId={existingOpportunityId}
        account={getValues('account')}
        onClose={onCloseRenewalModal}
        additionalFields={additionalFields}
      />
    );
  }

  const accountCurrency =
    (!isString(selectedAccountOption) &&
      selectedAccountOption?.defaultCurrency) ||
    internalFixedAccount?.defaultCurrency;

  return (
    <MCenterModal
      size="xl"
      isOpen={isOpen}
      onClose={onClose}
      closeOnOverlayClick={false}
      closeOnEsc={false}
      modalTitle="New Quote"
      renderFooter={() => (
        <MStack gap={4} direction="row" align="center" justify="right" flex={1}>
          <MButton
            variant="cancel"
            isDisabled={isNewQuoteLoading}
            onClick={handleModalCancel}
            data-testid="new-quote-cancel-new-account"
            minW="auto"
          >
            Cancel
          </MButton>

          {!!selectedAccountOption &&
            hasGuidedSellingProcess &&
            selectedContractId === NEW_CONTRACT_SLUG && (
              <MButton
                data-testid="new-quote-guided-selling-modal-btn"
                variant="secondary"
                onClick={() => submitModal(true)}
                isDisabled={isSubmitButtonDisable}
                minW="auto"
              >
                New Guided Quote
              </MButton>
            )}

          <MButton
            variant="primary"
            isLoading={isNewQuoteLoading || isNewAccountLoading}
            onClick={() => submitModal(false)}
            isDisabled={isSubmitButtonDisable}
            data-testid="new-quote-modal-btn"
            minW="auto"
          >
            {isNewAccount || isRenewal ? 'Next' : 'Create'}
          </MButton>
        </MStack>
      )}
    >
      <MBox>
        {isNewAccount ? (
          <NewAccountForm onChange={handleNewAccountChange} />
        ) : (
          <MVStack gap="3" align="flex-start">
            {!internalFixedAccount && (
              <MFormField label="Existing Account" isRequired>
                <Controller
                  name="account"
                  control={control}
                  defaultValue=""
                  render={({ field: { onChange, ...field } }) => (
                    <AccountSelect
                      onClearAccountValue={onClearAccountValue}
                      {...field}
                      onChange={(event) => {
                        const account = event as unknown as IAccountRespSchema;
                        onChange(account);
                        setValue('currency', account.defaultCurrency);
                        if (!isExistingContractSelected) {
                          setValue(
                            'legalEntityId',
                            account.defaultLegalEntityId,
                          );
                          setValue('currency', account.defaultCurrency);
                        }
                      }}
                      returnItem
                    />
                  )}
                />
              </MFormField>
            )}

            {!!internalFixedAccount && (
              <MVStack gap="1" align="flex-start">
                <MText fontWeight="bold">Account</MText>
                <MText>{internalFixedAccount.accountName}</MText>
              </MVStack>
            )}

            {!isAccountSelected && canDo([['accounts', 'create']]) && (
              <MHStack>
                <MText>Can&apos;t find the right account?</MText>
                <MButton
                  variant="tertiary"
                  onClick={() => setIsNewAccount(true)}
                  data-testid="new-quote-with-new-account-btn"
                >
                  Create New Account
                </MButton>
              </MHStack>
            )}

            {isAccountSelected && !isAmendOrRenew && accountCurrency && (
              <MFormField
                label="Create from Blueprint"
                tooltip="Choosing a Blueprint will create your quote to be structured the same way as the blueprint quote."
              >
                <BlueprintSelect
                  value={selectedBlueprintId}
                  onChange={handleBlueprintChange}
                  currency={selectedCurrency || accountCurrency}
                />
              </MFormField>
            )}

            {isAccountSelected && (
              <ContractSelectField
                accountId={
                  (!isString(selectedAccountOption) &&
                    selectedAccountOption?.id) ||
                  internalFixedAccount?.id ||
                  ''
                }
                contracts={contracts}
                setContracts={setContracts}
                setIsContractLoading={setIsContractLoading}
                isContractLoading={isContractLoading}
                selectedContract={selectedContract}
                setSelectedContract={setSelectedContract}
                newContractCurrency={crmCurrency || accountCurrency}
              />
            )}

            {isAccountSelected &&
              isExistingContractSelected &&
              !isContractLoading && (
                <MFormField
                  label="Do you want to amend or renew this contract?"
                  isRequired
                >
                  <Controller
                    name="quoteType"
                    control={control}
                    render={({ field: { onChange } }) => (
                      <MRadioGroup onChange={onChange}>
                        <MStack direction="row" gap={3}>
                          <MRadio
                            value={QuoteTypeEnum.AMENDMENT}
                            isDisabled={!canAmendContract(selectedContract!)}
                          >
                            Amend
                          </MRadio>
                          <MRadio
                            value={QuoteTypeEnum.RENEWAL}
                            isDisabled={!canRenewContract(selectedContract!)}
                          >
                            <MTooltip
                              label={
                                'Canceled Contracts cannot be renewed.  Instead, create a New Quote.'
                              }
                              placement="bottom-end"
                              isDisabled={
                                selectedContract?.status !==
                                ContractStatusEnum.CANCELED
                              }
                            >
                              Renew
                            </MTooltip>
                          </MRadio>
                        </MStack>
                      </MRadioGroup>
                    )}
                  />
                </MFormField>
              )}
            {isContractSelected && (
              <MFlex w="full" gap="4">
                {hasMultipleLegalEntity && (
                  <MFormField
                    error={formState.errors.legalEntityId}
                    label="Legal Entity"
                    tooltip="The entity selling to the customer."
                    isRequired
                  >
                    <Controller
                      name="legalEntityId"
                      control={control}
                      render={({ field }) => (
                        <MCustomSelect
                          isDisabled={isExistingContractSelected}
                          itemTitle="name"
                          itemValue="id"
                          items={legalEntities}
                          {...field}
                        />
                      )}
                    />
                  </MFormField>
                )}

                {hasMultipleCurrency && (
                  <MFormField
                    error={formState.errors.currency}
                    label="Currency"
                    isRequired
                  >
                    <Controller
                      name="currency"
                      control={control}
                      render={({ field }) => (
                        <MCurrencySelect
                          isDisabled={
                            isExistingContractSelected || !!selectedBlueprintId
                          }
                          {...field}
                        />
                      )}
                    />
                  </MFormField>
                )}
              </MFlex>
            )}
            {isContractSelected &&
              !existingOpportunityId &&
              hasCrmConfigured && (
                <OpportunitySearchInput
                  accountId={
                    isString(selectedAccountOption)
                      ? selectedAccountOption
                      : selectedAccountOption?.id ||
                        internalFixedAccount?.id ||
                        ''
                  }
                  crmOpportunityId={crmOpportunity?.id}
                  onChange={setCrmOpportunity}
                  showDivider
                />
              )}

            {isNewContractSelected && (
              <MFormField
                label="Linked Account"
                tooltip="Optional account, usually a partner or reseller or customer. Used when quoting on behalf of another party."
              >
                <Controller
                  name="linkedAccountId"
                  control={control}
                  defaultValue=""
                  render={({ field: { onChange, value, ...field } }) => {
                    const primaryAccountId = isString(selectedAccountOption)
                      ? selectedAccountOption
                      : selectedAccountOption?.id ||
                        internalFixedAccount?.id ||
                        '';

                    if (hasHubspotConfigured && existingOpportunityId) {
                      return (
                        <HubspotLinkedAccountSelect
                          opportunityId={existingOpportunityId}
                          value={value as string}
                          disabledIds={
                            primaryAccountId ? [primaryAccountId] : []
                          }
                          onSelectHubspotCompany={(company) => {
                            setHubspotLinkedCompany(company);
                            onChange(company?.monetizeAccountId ?? '');
                          }}
                          onSelectStandardAccount={(account) => {
                            setHubspotLinkedCompany(null);
                            onChange(account.id);
                          }}
                          onClearAccountValue={() => {
                            setHubspotLinkedCompany(null);
                            onChange('');
                          }}
                        />
                      );
                    }

                    return (
                      <AccountSelect
                        onClearAccountValue={() => onChange('')}
                        value={value}
                        {...field}
                        onChange={(event) => {
                          const account =
                            event as unknown as IAccountRespSchema;
                          setHubspotLinkedCompany(null);
                          onChange(account);
                        }}
                        disabledIds={primaryAccountId ? [primaryAccountId] : []}
                        returnItem={false}
                      />
                    );
                  }}
                />
              </MFormField>
            )}
          </MVStack>
        )}
      </MBox>
    </MCenterModal>
  );
};
